import React, { useEffect, useState } from "react";
import axios from "axios";
import "./Rightbar.css";
import { Users, CirclePile, CircleStar, Check, Plus, ChevronRight } from "lucide-react";

export default function Rightbar({ isOpen, onClose }) {
  const [recommendedUsers, setRecommendedUsers] = useState([]);
  const [recommendedCircles, setRecommendedCircles] = useState([]);
  const [connectedCircles, setConnectedCircles] = useState([]);
  const token = localStorage.getItem("token");

  useEffect(() => {
    const fetchRightbarData = async () => {
      try {
        const [usersRes, circlesRes, connectedRes] = await Promise.all([
          axios.get(`${process.env.REACT_APP_API_URL}/suggestions`, {
            headers: { Authorization: `Bearer ${token}` }
          }),
          axios.get(`${process.env.REACT_APP_API_URL}/circles/recommended`, {
            headers: { Authorization: `Bearer ${token}` }
          }),
          axios.get(`${process.env.REACT_APP_API_URL}/circles/connected`, {
            headers: { Authorization: `Bearer ${token}` }
          })
        ]);

        setRecommendedUsers(usersRes.data.slice(0, 4));
        setRecommendedCircles(circlesRes.data.slice(0, 3));
        setConnectedCircles(connectedRes.data.slice(0, 3));
      } catch (err) {
        console.error("Rightbar fetch failed", err);
      }
    };

    fetchRightbarData();
  }, [token]);

  const handleConnect = async (userId) => {
    try {
      await axios.post(
        `${process.env.REACT_APP_API_URL}/network/connect/${userId}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setRecommendedUsers(prev =>
        prev.map(user =>
          user._id === userId ? { ...user, connected: true } : user
        )
      );
    } catch (err) {
      console.error("Connection failed", err);
    }
  };

  const handleJoinCircle = async (circleId) => {
    try {
      await axios.post(
        `${process.env.REACT_APP_API_URL}/circles/join/${circleId}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      // Move from recommended to connected
      const joinedCircle = recommendedCircles.find(c => c._id === circleId);
      setRecommendedCircles(prev => prev.filter(c => c._id !== circleId));
      setConnectedCircles(prev => [joinedCircle, ...prev].slice(0, 3));
    } catch (err) {
      console.error("Failed to join circle", err);
    }
  };

  return (
    <>
      {/* Mobile Overlay */}
      {isOpen && <div className="rightbar-overlay" onClick={onClose} />}
      
      <aside className={`rightbar-minimal ${isOpen ? 'open' : ''}`}>
        {/* Header for mobile */}
        <div className="rightbar-header">
          <h3>Discover</h3>
          <button className="close-btn" onClick={onClose}>×</button>
        </div>

        {/* Recommended Connections */}
        <div className="rightbar-section">
          <div className="section-header">
            <Users size={18} />
            <h4>Recommended Connections</h4>
          </div>
          
          <div className="users-list">
            {recommendedUsers.length > 0 ? (
              recommendedUsers.map((user) => (
                <div key={user._id} className="user-row">
                  <img
                    src={user.profilepic ? `${process.env.REACT_APP_FILE_URL}${user.profilepic}` : "/default-avatar.png"}
                    alt={user.name}
                    className="user-avatar"
                  />
                  <div className="user-details">
                    <span className="user-name">{user.name}</span>
                    <span className="user-role">{user.role || "Member"}</span>
                  </div>
                  <button
                    className={`connect-button ${user.connected ? 'connected' : ''}`}
                    onClick={() => handleConnect(user._id)}
                    disabled={user.connected}
                  >
                    {user.connected ? <Check size={14} /> : <Plus size={14} />}
                    <span>{user.connected ? 'Connected' : 'Connect'}</span>
                  </button>
                </div>
              ))
            ) : (
              <div className="empty-state">No recommendations</div>
            )}
          </div>
          
          <button className="view-all-link">
            <span>View all</span>
            <ChevronRight size={14} />
          </button>
        </div>

        {/* Recommended Circles */}
        <div className="rightbar-section">
          <div className="section-header">
            <CircleStar size={18} />
            <h4>Recommended Circles</h4>
          </div>
          
          <div className="circles-list">
            {recommendedCircles.length > 0 ? (
              recommendedCircles.map((circle) => (
                <div key={circle._id} className="circle-row">
                  <div className="circle-icon">
                    {circle.icon || <CircleStar size={20} />}
                  </div>
                  <div className="circle-details">
                    <span className="circle-name">{circle.name}</span>
                    <span className="circle-meta">{circle.members} members</span>
                  </div>
                  <button
                    className="join-button"
                    onClick={() => handleJoinCircle(circle._id)}
                  >
                    <Plus size={14} />
                    <span>Join</span>
                  </button>
                </div>
              ))
            ) : (
              <div className="empty-state">No circles to show</div>
            )}
          </div>
        </div>

        {/* Connected Circles */}
        <div className="rightbar-section">
          <div className="section-header">
            <CirclePile size={18} />
            <h4>Your Circles</h4>
          </div>
          
          <div className="circles-list">
            {connectedCircles.length > 0 ? (
              connectedCircles.map((circle) => (
                <div key={circle._id} className="circle-row connected">
                  <div className="circle-icon">
                    <CirclePile size={20} />
                  </div>
                  <div className="circle-details">
                    <span className="circle-name">{circle.name}</span>
                    <span className="circle-meta">{circle.members} members</span>
                  </div>
                  <span className="active-badge">Active</span>
                </div>
              ))
            ) : (
              <div className="empty-state">No circles joined yet</div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="rightbar-footer">
          <p>© 2024 UniCircle</p>
        </div>
      </aside>
    </>
  );
}